import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Skeleton,
    InputAdornment,
    IconButton,
    Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { observer } from 'mobx-react-lite';
import CommonInput from '../../component/form/common-input.component';
import LinechartOfBusinessData, {
    mergeBusinessLineApexOptions,
    createDashboardBusinessLineApexOptions,
} from '../../component/charts/line-chart-of-business-data';
import i18n from '../../i18n';
import { formatEUR } from '../../constants/tool.fn';
import groupRevenueAnalysisState from './group-revenue-analysis-state';
import { accountState } from '../my-account/account-state';
import {
    MenuSalesMetric,
    type GroupMenuCatalogOptionItem,
    type MenuSalesByMenuNameRankingItem,
} from '../../request/api/group-revenue-analysis';
import { DishSalesSearchDialog, type DishSalesSearchRow } from './dish-sales-search-result-table.component';
import { ReactComponent as SearchIconSvg } from '../../static/images/svg/ic_round-search.svg';
import { ReactComponent as PillSelectStoreIcon } from '../../static/images/svg/bxs_store.svg';
import {
    GROUP_REVENUE_DISH_SALES_TAB_FILL_VIEWPORT_MIN_HEIGHT_PX,
    GROUP_REVENUE_DISH_SALES_TAB_TWO_COLUMN_MIN_WIDTH_PX,
    GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX,
} from './group-revenue-analysis-layout';

const chartLabelColor = 'var(--text-neutral-500)';
const chartGridColor = 'var(--stroke-neutral-800-a25)';

/** 工具栏搜索框：停止输入后自动打开弹窗的防抖间隔（毫秒） */
// const DISH_SALES_SEARCH_TOOLBAR_DEBOUNCE_MS = 400;

/** 折线图指标：销售金额 / 销售数量 */
const METRIC_OPTIONS = [
    { value: MenuSalesMetric.Amount, label: 'group_revenue_analysis_menu_sales_metric_amount' },
    { value: MenuSalesMetric.Quantity, label: 'group_revenue_analysis_menu_sales_metric_quantity' },
];

/** Apex stroke.dashArray：0 为实线，大于 0 为虚线节长 */
const STROKE_SOLID = 0;
const STROKE_DASH_AVERAGE = 6;

/** 弹窗子类别选项 value：JSON [menu_name, category_name] */
function categoryRowSelectValue(c: GroupMenuCatalogOptionItem): string {
    return JSON.stringify([c.menu_name ?? '', c.name]);
}

/**
 * 趋势图副标题 / 单店曲线图例回退名：与筛选维度一致（优先级 菜品名 → 菜品类别名 → 菜单名，最后回退接口 `menu_name`）。
 */
function dishSalesTrendFilterDisplayName(
    dishName: string,
    categoryName: string,
    menuName: string,
    salesMenuNameFallback?: string | null
): string {
    const d = dishName.trim();
    if (d) return d;
    const c = categoryName.trim();
    if (c) return c;
    const m = menuName.trim();
    if (m) return m;
    return (salesMenuNameFallback ?? '').trim();
}

/**
 * 集团菜单销售分析 Tab（Figma）：对比门店与菜单维度筛选、趋势折线图、门店菜单营收排名
 */
const GroupDishSalesAnalysis: React.FC = observer(() => {
    const theme = useTheme();
    /** 宽屏双栏：≥1280px 时左折线、右排名并排 */
    const isTwoColumnLayout = useMediaQuery(
        `(min-width: ${GROUP_REVENUE_DISH_SALES_TAB_TWO_COLUMN_MIN_WIDTH_PX}px)`,
    );
    /** 大屏拉满：需同时满足足够高度与双栏宽度 */
    const fillsViewportOnLargeScreen = useMediaQuery(
        `(min-height: ${GROUP_REVENUE_DISH_SALES_TAB_FILL_VIEWPORT_MIN_HEIGHT_PX}px) and (min-width: ${GROUP_REVENUE_DISH_SALES_TAB_TWO_COLUMN_MIN_WIDTH_PX}px)`,
    );
    const state = groupRevenueAnalysisState;
    const groups = accountState.userInfo?.responsible_group ?? [];
    const groupId = state.selectedGroupStringId || (groups[0]?.string_id ?? '');

    const catalog = state.menuCatalogOptions;
    const sales = state.menuSalesByMenuName;

    /** 工具栏搜索框文案（不参与接口，仅打开弹窗时带入） */
    /** 菜单搜索弹窗 */
    const [searchModalOpen, setSearchModalOpen] = useState(false);
    const [modalSearchDraft, setModalSearchDraft] = useState('');
    /** 虚拟列表中选中的结果行 key（确认后写入主筛选） */
    const [selectedSearchRowKey, setSelectedSearchRowKey] = useState<string | null>(null);

    /** 工具栏输入防抖定时器（卸载时清除，避免泄漏） */
    const dishSalesSearchToolbarDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(
        () => () => {
            if (dishSalesSearchToolbarDebounceRef.current != null) {
                clearTimeout(dishSalesSearchToolbarDebounceRef.current);
            }
        },
        []
    );

    /** 切换集团时清空工具栏搜索并关闭弹窗 */
    const prevGroupIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (prevGroupIdRef.current != null && prevGroupIdRef.current !== groupId) {
            setSearchModalOpen(false);
            setModalSearchDraft('');
            setSelectedSearchRowKey(null);
        }
        prevGroupIdRef.current = groupId;
    }, [groupId]);

    const compareStoreKey = state.dishSalesCompareStoreIds.join('|');

    /** 进入 Tab 或集团变更：先拉菜单合集 */
    useEffect(() => {
        if (!groupId) return;
        void groupRevenueAnalysisState.fetchMenuCatalogOptions(groupId);
    }, [groupId, compareStoreKey]);

    /**
     * 菜单列表从「无」变为「有」且当前未选菜单时，默认选中第一项（避免用户选「无」后误判：用 prevLen===0 区分）
     */
    const prevMenuCatalogCountRef = useRef(0);
    /** 仅换集团时重置：换对比门店不重置，避免旧 menus 长度与 prevLen===0 误触发默认选中 */
    useEffect(() => {
        prevMenuCatalogCountRef.current = 0;
    }, [groupId]);

    useEffect(() => {
        const menus = state.menuCatalogMenus;
        const len = menus.length;
        const prevLen = prevMenuCatalogCountRef.current;
        if (len > 0 && prevLen === 0 && !state.dishSalesMenuName.trim()) {
            state.setDishSalesMenuName(menus[0].name);
        }
        prevMenuCatalogCountRef.current = len;
    }, [state.menuCatalogMenus, state.dishSalesMenuName, state]);

    /** 时间、筛选、指标变更：拉销售分析（搜索仅本地，不传接口） */
    useEffect(() => {
        if (!groupId) return;
        void groupRevenueAnalysisState.fetchMenuSalesByMenuNameIfNeeded(groupId);
    }, [
        groupId,
        state.startDate,
        state.endDate,
        compareStoreKey,
        state.dishSalesMenuName,
        state.dishSalesCategoryName,
        state.dishSalesDishName,
    ]);

    const noneLabel = i18n.t('group_revenue_analysis_filter_none');

    const noneOption = useMemo(() => ({ value: '', label: noneLabel }), [noneLabel]);

    const searchFilterNorm = modalSearchDraft.trim().toLowerCase();

    /** 弹窗内：在一级菜单列表中按关键词过滤（数据同源 menuCatalogMenus） */
    const filteredMenusForSearch = useMemo(() => {
        const list = state.menuCatalogMenus;
        if (!searchFilterNorm) return list;
        return list.filter((m) => m.name.toLowerCase().includes(searchFilterNorm));
    }, [state.menuCatalogMenus, searchFilterNorm]);

    /** 弹窗内：子类别在 catalog.categories 中过滤 */
    const filteredCategoriesForSearch = useMemo(() => {
        const list = catalog?.categories ?? [];
        if (!searchFilterNorm) return list;
        return list.filter(
            (c) =>
                c.name.toLowerCase().includes(searchFilterNorm) ||
                (c.menu_name ?? '').toLowerCase().includes(searchFilterNorm)
        );
    }, [catalog?.categories, searchFilterNorm]);

    /** 弹窗内：菜品在 catalog.dishes 中过滤 */
    const filteredDishesForSearch = useMemo(() => {
        const list = catalog?.dishes ?? [];
        if (!searchFilterNorm) return list;
        return list.filter((d) => {
            const q = searchFilterNorm;
            return (
                d.name.toLowerCase().includes(q) ||
                (d.menu_name ?? '').toLowerCase().includes(q) ||
                (d.category_name ?? '').toLowerCase().includes(q)
            );
        });
    }, [catalog?.dishes, searchFilterNorm]);

    /** 菜单 / 类别 / 菜品三路合并结果（有关键词时才有行） */
    const searchResultRows = useMemo((): DishSalesSearchRow[] => {
        if (!searchFilterNorm) return [];
        const menus: DishSalesSearchRow[] = filteredMenusForSearch.map((m) => ({
            kind: 'menu',
            key: `menu:${m.name}`,
            menuName: m.name,
        }));
        const cats: DishSalesSearchRow[] = filteredCategoriesForSearch.map((c) => ({
            kind: 'category',
            key: `cat:${categoryRowSelectValue(c)}`,
            menuName: (c.menu_name ?? '').trim(),
            categoryName: c.name,
        }));
        const dishes: DishSalesSearchRow[] = filteredDishesForSearch.map((d) => ({
            kind: 'dish',
            key: `dish:${d.id}`,
            dish: d,
        }));
        return [...menus, ...cats, ...dishes];
    }, [searchFilterNorm, filteredMenusForSearch, filteredCategoriesForSearch, filteredDishesForSearch]);

    /** 关键词变更后清空选中，避免 key 与当前列表不一致 */
    useEffect(() => {
        setSelectedSearchRowKey(null);
    }, [modalSearchDraft]);

    /** 列表刷新后若选中 key 已不在结果中则清空 */
    useEffect(() => {
        if (selectedSearchRowKey == null) return;
        if (!searchResultRows.some((r) => r.key === selectedSearchRowKey)) {
            setSelectedSearchRowKey(null);
        }
    }, [searchResultRows, selectedSearchRowKey]);

    /** 已选一行且有关键词时可确认 */
    const canApplyModalSearch = Boolean(searchFilterNorm && selectedSearchRowKey);

    /**
     * 打开搜索弹窗：带入工具栏关键词并清空待选维度。
     * @param toolbarKeyword 可选；不传则使用当前 `keywordDraft`（Enter / 放大镜与防抖回调需一致）
     */
    const openDishSalesSearchModal = (toolbarKeyword?: string) => {
        setModalSearchDraft(toolbarKeyword ?? '');
        setSelectedSearchRowKey(null);
        setSearchModalOpen(true);
    };

    /** 弹窗内重置：清空关键词与选中行 */
    const handleModalSearchReset = () => {
        setModalSearchDraft('');
        setSelectedSearchRowKey(null);
    };

    /** 应用当前选中行（菜单 / 类别 / 菜品） */
    const handleModalSearchApply = () => {
        if (!canApplyModalSearch || !selectedSearchRowKey) return;
        const picked = searchResultRows.find((r) => r.key === selectedSearchRowKey);
        if (!picked) return;
        if (picked.kind === 'menu') {
            state.setDishSalesMenuName(picked.menuName);
            state.setDishSalesCategoryName('');
            state.setDishSalesDishName('');
        } else if (picked.kind === 'category') {
            state.setDishSalesMenuName(picked.menuName);
            state.setDishSalesCategoryName(picked.categoryName);
            state.setDishSalesDishName('');
        } else {
            const d = picked.dish;
            state.setDishSalesMenuName(d.menu_name ?? '');
            state.setDishSalesCategoryName(d.category_name ?? '');
            state.setDishSalesDishName(d.name);
        }
        setSearchModalOpen(false);
    };

    const selectedMenuName = state.dishSalesMenuName.trim();
    const selectedCategoryName = state.dishSalesCategoryName.trim();

    /**
     * 已选菜单下的子类别：
     * - 有 `menu_name` 的项按菜单过滤；空 `menu_name` 视为全菜单通用。
     * - 若扁平 categories 全部无 `menu_name`（切换菜单时列表永远相同），则从 `dishes` 上带菜单维度的记录推断类别。
     */
    const categoriesForSelectedMenu = useMemo((): GroupMenuCatalogOptionItem[] => {
        const all = catalog?.categories ?? [];
        const dishes = catalog?.dishes ?? [];
        if (!selectedMenuName) return [];

        const anyCategoryScoped = all.some((c) => (c.menu_name ?? '').trim() !== '');
        if (anyCategoryScoped) {
            return all.filter((c) => {
                const mn = (c.menu_name ?? '').trim();
                return mn === '' || mn === selectedMenuName;
            });
        }

        const dishMenuScoped = dishes.some((d) => (d.menu_name ?? '').trim() !== '');
        if (dishMenuScoped) {
            const names = new Set<string>();
            for (const d of dishes) {
                const mn = (d.menu_name ?? '').trim();
                const cn = (d.category_name ?? '').trim();
                if (!cn) continue;
                if (mn === '' || mn === selectedMenuName) names.add(cn);
            }
            return Array.from(names)
                .sort()
                .map((name) => ({
                    id: name,
                    name,
                    menu_name: selectedMenuName,
                }));
        }

        return all.filter((c) => {
            const mn = (c.menu_name ?? '').trim();
            return mn === '' || mn === selectedMenuName;
        });
    }, [catalog?.categories, catalog?.dishes, selectedMenuName]);

    /**
     * 已选菜单 + 子类下的菜品：
     * - 缺 menu_name / category_name 的项视为不限制该维度。
     * - 若严格过滤结果为空，且全量菜品均未带 menu_name，则仅按 category_name 匹配（兼容扁平列表未写菜单维度）。
     */
    const dishesForSelectedMenuAndCategory = useMemo((): GroupMenuCatalogOptionItem[] => {
        const all = catalog?.dishes ?? [];
        if (!selectedMenuName || !selectedCategoryName) return [];

        const strict = all.filter((d) => {
            const mn = (d.menu_name ?? '').trim();
            const cn = (d.category_name ?? '').trim();
            const menuOk = mn === '' || mn === selectedMenuName;
            const catOk = cn === '' || cn === selectedCategoryName;
            return menuOk && catOk;
        });
        if (strict.length > 0) return strict;

        const anyDishHasMenu = all.some((d) => (d.menu_name ?? '').trim() !== '');
        if (!anyDishHasMenu) {
            return all.filter((d) => {
                const cn = (d.category_name ?? '').trim();
                return cn === selectedCategoryName || cn === '';
            });
        }

        return [];
    }, [catalog?.dishes, selectedMenuName, selectedCategoryName]);

    /** 菜单变更时清理不再属于当前菜单的类别/菜品；类别变更时清理菜品 */
    useEffect(() => {
        if (!selectedMenuName) {
            if (state.dishSalesCategoryName) state.setDishSalesCategoryName('');
            if (state.dishSalesDishName) state.setDishSalesDishName('');
            return;
        }
        const catSet = new Set(categoriesForSelectedMenu.map((c) => c.name));
        if (selectedCategoryName && !catSet.has(selectedCategoryName)) {
            state.setDishSalesCategoryName('');
            state.setDishSalesDishName('');
        }
    }, [selectedMenuName, categoriesForSelectedMenu, selectedCategoryName, state]);

    useEffect(() => {
        if (!selectedMenuName || !selectedCategoryName) {
            if (state.dishSalesDishName) state.setDishSalesDishName('');
            return;
        }
        const dishSet = new Set(dishesForSelectedMenuAndCategory.map((d) => d.name));
        if (state.dishSalesDishName && !dishSet.has(state.dishSalesDishName)) {
            state.setDishSalesDishName('');
        }
    }, [
        selectedMenuName,
        selectedCategoryName,
        dishesForSelectedMenuAndCategory,
        state.dishSalesDishName,
        state,
    ]);

    const currentGroup = useMemo(
        () => {
            const userInfo = accountState.userInfo;
            // 拥有的餐厅对应的集团；
            const groups = userInfo?.groups ?? [];
            // 集团负责人
            const responsibleGroup = userInfo?.responsible_group ?? [];
            // 拥有的餐厅的餐厅昵称
            const restaurantNicknames: { [key: string]: string } = userInfo?.restaurantNicknames ?? {};
            // 拥有的餐厅对应的集团列表；
            const ownedGroup = responsibleGroup.map((g) => {
                // 找到集团负责人对应的集团
                const ownedGroup = groups.find((g2) => g2._id === g.string_id);
                // 返回集团负责人对应的集团
                return {
                    value: g.string_id,
                    label: g.nickname,
                    // 返回集团负责人对应的集团拥有的餐厅
                    owned_restaurant: ownedGroup ? g.owned_restaurant?.map((r) => {
                        const restaurant = ownedGroup.restaurants?.find((rs) => rs === r);
                        if (restaurant) {
                            return {
                                value: r,
                                label: restaurantNicknames[r],
                            }
                        }
                        return null;
                    }).filter((r) => r !== null) || [] : []
                }
            })
            return ownedGroup.find((g) => g.value === groupId);
        },
        [groupId, accountState.userInfo]
    );

    /** 对比门店：当前集团 `owned_restaurant` 顺序，用 `restaurants` 补全名称与 string_id */
    const restaurantItems: { value: string, label: string }[] = useMemo(() => {
        const g = currentGroup;
        if (!g) return [];
        const owned = g.owned_restaurant ?? [];
        if (owned.length === 0) return [];
        return owned
    }, [currentGroup]) as { value: string, label: string }[];

    const restaurantIdToLabel = useMemo(() => {
        const m = new Map<string, string>();
        for (const r of restaurantItems) m.set(r.value, r.label ?? r.value);
        return m;
    }, [restaurantItems]);

    /** 下拉 value 使用名称，与接口 menu_name / category_name / dish_name 一致 */
    /** 一级菜单选项：来自接口 `res.menus` 在 state 中的归一化副本 `menuCatalogMenus` */
    const menuSelectItems = useMemo(
        () => [noneOption, ...state.menuCatalogMenus.map((m) => ({ value: m.name, label: m.name }))],
        [noneOption, state.menuCatalogMenus]
    );
    const categorySelectItems = useMemo(
        () => [
            noneOption,
            ...categoriesForSelectedMenu.map((c) => ({ value: c.name, label: c.name })),
        ],
        [noneOption, categoriesForSelectedMenu]
    );
    const dishSelectItems = useMemo(
        () => [
            noneOption,
            ...dishesForSelectedMenuAndCategory.map((d) => ({ value: d.name, label: d.name })),
        ],
        [noneOption, dishesForSelectedMenuAndCategory]
    );

    const isQuantityMetric = state.dishSalesMetric === MenuSalesMetric.Quantity;

    /**
     * 折线图：优先 `restaurant_curves` 各店实线；若有 `average_curve.points` 则追加一条平均线（虚线）。
     * 无各店曲线时回退仅展示 `average_curve` 一条实线（图例名与当前筛选维度一致）。
     */
    const { lineChartRawXLabels, lineChartSeries, lineChartStrokeDashArray } = useMemo(() => {
        const empty = {
            lineChartRawXLabels: [] as string[],
            lineChartSeries: [] as { name: string; data: (number | null)[] }[],
            lineChartStrokeDashArray: [] as number[],
        };
        const curves = sales?.restaurant_curves ?? [];
        const avgPoints = sales?.average_curve?.points ?? [];
        if (curves.length > 0) {
            const xSet = new Set<string>();
            for (const c of curves) {
                for (const p of c.points) xSet.add(p.x);
            }
            for (const p of avgPoints) xSet.add(p.x);
            const xs = Array.from(xSet).sort((a, b) => a.localeCompare(b));
            const series = curves.map((c) => {
                const name =
                    c.restaurant_name.trim() ||
                    (c.restaurant_id ? restaurantIdToLabel.get(c.restaurant_id) ?? c.restaurant_id : '');
                const map = new Map(
                    c.points.map((p) => [
                        p.x,
                        isQuantityMetric ? Math.round(p.quantity) : p.amount,
                    ] as const)
                );
                const data = xs.map((x) => (map.has(x) ? (map.get(x) as number) : null));
                return { name: name || '—', data };
            });
            const dashArray = curves.map(() => STROKE_SOLID);
            if (avgPoints.length > 0) {
                const avgMap = new Map(
                    avgPoints.map((p) => [
                        p.x,
                        isQuantityMetric ? Math.round(p.quantity) : p.amount,
                    ] as const)
                );
                const avgData = xs.map((x) => (avgMap.has(x) ? (avgMap.get(x) as number) : null));
                series.push({
                    name: i18n.t('group_revenue_analysis_menu_sales_average_series'),
                    data: avgData,
                });
                dashArray.push(STROKE_DASH_AVERAGE);
            }
            return {
                lineChartRawXLabels: xs,
                lineChartSeries: series,
                lineChartStrokeDashArray: dashArray,
            };
        }
        const list = avgPoints;
        if (list.length === 0) return empty;
        const rawXLabels = list.map((p) => p.x);
        const data = list.map((p) => (isQuantityMetric ? Math.round(p.quantity) : p.amount));
        const fallbackName = dishSalesTrendFilterDisplayName(
            state.dishSalesDishName,
            state.dishSalesCategoryName,
            state.dishSalesMenuName,
            sales?.menu_name
        );
        return {
            lineChartRawXLabels: rawXLabels,
            lineChartSeries: [{ name: fallbackName || '—', data }],
            lineChartStrokeDashArray: [STROKE_SOLID],
        };
    }, [
        sales,
        restaurantIdToLabel,
        isQuantityMetric,
        i18n.language,
        state.dishSalesDishName,
        state.dishSalesCategoryName,
        state.dishSalesMenuName,
    ]);

    /** 对比折线图 Apex 片段（轴、tooltip 金额格式） */
    const dishLineCompareExtra = useMemo(
        () => ({
            xaxis: {
                labels: { style: { colors: chartLabelColor, fontSize: '12px' } },
            },
            yaxis: {
                labels: {
                    style: { colors: chartLabelColor, fontSize: '12px' },
                    formatter: (value: number) =>
                        isQuantityMetric ? String(Math.round(value)) : String(value),
                },
            },
            grid: { borderColor: chartGridColor },
            tooltip: {
                enabled: true,
                shared: true,
                intersect: false,
                followCursor: false,
                theme: theme.palette.mode,
                y: {
                    formatter: (val: number) =>
                        isQuantityMetric ? String(Math.round(val)) : formatEUR(val),
                },
            },
        }),
        [chartGridColor, chartLabelColor, isQuantityMetric, theme.palette.mode],
    );

    /** 菜品趋势折线图 Apex options */
    const dishLineApexOptions = useMemo(
        () =>
            mergeBusinessLineApexOptions(
                createDashboardBusinessLineApexOptions(theme, {
                    rawXLabels: lineChartRawXLabels,
                    seriesCount: lineChartSeries.length,
                    height: fillsViewportOnLargeScreen ? '100%' : GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX,
                    /** 使用 Apex 内置图例（含仅一条曲线时） */
                    showLegend: lineChartSeries.length > 0,
                    strokeWidth: 2,
                    strokeDashArray:
                        lineChartStrokeDashArray.length === lineChartSeries.length
                            ? lineChartStrokeDashArray
                            : undefined,
                }),
                dishLineCompareExtra,
            ),
        [
            theme,
            lineChartRawXLabels,
            lineChartSeries,
            lineChartStrokeDashArray,
            dishLineCompareExtra,
            fillsViewportOnLargeScreen,
        ],
    );

    /** 趋势标题「—」后文案：有菜品显菜品名，有类别显类别名，否则显菜单名 */
    const chartTitleMenuName = dishSalesTrendFilterDisplayName(
        state.dishSalesDishName,
        state.dishSalesCategoryName,
        state.dishSalesMenuName,
        sales?.menu_name
    );

    /** 门店菜单营收排名：与集团总营收侧栏一致，第一名满宽进度条，其余相对第一名比例 */
    const menuStoreRevenueRankingBars = useMemo(() => {
        const rankMetric = (r: MenuSalesByMenuNameRankingItem) =>
            isQuantityMetric && r.quantity != null ? r.quantity : r.amount;
        const rankingList = [...(sales?.ranking ?? [])].sort(
            (a, b) => rankMetric(b) - rankMetric(a),
        );
        if (rankingList.length === 0) return null;
        const firstVal = rankMetric(rankingList[0]);
        return rankingList.map((row, i) => {
            const rowVal = rankMetric(row);
            const barWidth = firstVal > 0 ? (rowVal / firstVal) * 100 : 0;
            const isFullWidth = barWidth >= 100;
            return (
                <Box
                    key={`${row.restaurant_name}-${row.menu_name}-${i}`}
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexShrink: 0,
                        px: 4,
                        py: 3,
                        borderRadius: 4,
                        bgcolor: 'var(--fill-neutral-200)',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: `${barWidth}%`,
                            bgcolor: 'var(--fill-primary-200)',
                            ...(isFullWidth
                                ? { borderRadius: 2 }
                                : { borderTopLeftRadius: 8, borderBottomLeftRadius: 8 }),
                            zIndex: 0,
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            color: 'var(--text-dark)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            minWidth: 0,
                            mr: 1,
                        }}
                    >
                        {row.restaurant_name}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            color: 'var(--text-primary-300)',
                            fontWeight: 600,
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {isQuantityMetric && row.quantity != null
                            ? String(Math.round(row.quantity))
                            : formatEUR(row.amount)}
                    </Typography>
                </Box>
            );
        });
    }, [sales?.ranking, isQuantityMetric]);

    const handleClearFilters = () => {
        state.setDishSalesCompareStoreIds([]);
        state.setDishSalesMenuName('');
        state.setDishSalesCategoryName('');
        state.setDishSalesDishName('');
        setSearchModalOpen(false);
        handleModalSearchReset();
    };

    const filterRow = (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
                rowGap: 2,
                flexShrink: 0,
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    fontSize: 24,
                    lineHeight: '32px',
                    fontWeight: 500,
                    color: 'var(--text-dark)',
                    whiteSpace: 'nowrap',
                }}
            >
                {i18n.t('group_revenue_analysis_compare_stores')}
            </Typography>
            <CommonInput
                inputType="SELECT_PILL_MUL"
                size="small"
                value={state.dishSalesCompareStoreIds}
                onChange={(e) => {
                    state.setDishSalesCompareStoreIds((e.target?.value as unknown as string[]) ?? []);
                }}
                items={restaurantItems}
                placeholder={i18n.t('group_revenue_analysis_all_stores')}
                pillMultiPlaceholderFontSize={20}
            />
            <CommonInput
                inputType="SELECT_PILL"
                size="small"
                labelPrefix={i18n.t('group_revenue_analysis_filter_menu_prefix')}
                items={menuSelectItems}
                value={state.dishSalesMenuName}
                onChange={(e) => state.setDishSalesMenuName(e.target.value)}
                sx={{ flexShrink: 0, minWidth: 200 }}
            />
            <CommonInput
                inputType="SELECT_PILL"
                size="small"
                labelPrefix={i18n.t('group_revenue_analysis_filter_category_prefix')}
                items={categorySelectItems}
                value={state.dishSalesCategoryName}
                onChange={(e) => state.setDishSalesCategoryName(String(e.target.value))}
                sx={{ flexShrink: 0, minWidth: 220 }}
            />
            <CommonInput
                inputType="SELECT_PILL"
                size="small"
                labelPrefix={i18n.t('group_revenue_analysis_filter_dish_prefix')}
                items={dishSelectItems}
                value={state.dishSalesDishName}
                onChange={(e) => state.setDishSalesDishName(String(e.target.value))}
                sx={{ flexShrink: 0, minWidth: 220 }}
                virtualizePillMenu
            />
            <Button
                variant="text"
                size="small"
                onClick={handleClearFilters}
                sx={{
                    color: 'var(--text-dark)',
                    textTransform: 'none',
                    fontSize: 18,
                    minWidth: 'auto',
                    px: 2,
                    borderRadius: '8px',
                    boxShadow: '0px 0px 4px rgba(0,0,0,0.25)',
                    bgcolor: 'var(--fill-neutral-100)',
                }}
            >
                {i18n.t('group_revenue_analysis_clear_filters')}
            </Button>
            <Box sx={{ flexShrink: 0, ml: 'auto' }}>
                <CommonInput
                    inputType="INPUT"
                    size="small"
                    placeholder={i18n.t('group_revenue_analysis_search_placeholder')}
                    onClick={(e) => {
                        // 去掉对焦效果
                        (e.target as HTMLInputElement).blur();
                        openDishSalesSearchModal();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            openDishSalesSearchModal();
                        }
                    }}
                    sx={{
                        minWidth: 160,
                        maxWidth: 160,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '16px',
                            bgcolor: 'var(--fill-neutral-100)',
                            boxShadow: '0px 0px 2px var(--shadow-25)',
                        },
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    type="button"
                                    size="small"
                                    edge="end"
                                    tabIndex={-1}
                                    aria-label="search"
                                    onClick={() => openDishSalesSearchModal()}
                                >
                                    <SearchIconSvg style={{ width: 22, height: 22 }} />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
        </Box>
    );

    /** 菜单本地搜索弹窗（与骨架屏、主内容共用，避免加载时关闭后无法再次打开） */
    const dishSalesSearchDialog = (
        <DishSalesSearchDialog
            open={searchModalOpen}
            onClose={() => setSearchModalOpen(false)}
            searchDraft={modalSearchDraft}
            onSearchDraftChange={setModalSearchDraft}
            searchResultRows={searchResultRows}
            selectedSearchRowKey={selectedSearchRowKey}
            onSelectSearchRowKey={setSelectedSearchRowKey}
            canApply={canApplyModalSearch}
            onApply={handleModalSearchApply}
            emptyTableText={i18n.t('group_revenue_analysis_search_empty')}
        />
    );

    if (state.menuSalesLoading || state.menuCatalogOptionsLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    ...(fillsViewportOnLargeScreen ? { flex: 1, minHeight: 0, overflow: 'hidden' } : {}),
                }}
            >
                <GroupDishSalesAnalysisSkeleton
                    filterRow={filterRow}
                    fillsViewportOnLargeScreen={fillsViewportOnLargeScreen}
                    isTwoColumnLayout={isTwoColumnLayout}
                />
                {dishSalesSearchDialog}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                ...(fillsViewportOnLargeScreen
                    ? { flex: 1, minHeight: 0, overflow: 'hidden' }
                    : {}),
            }}
        >
            {filterRow}
            {dishSalesSearchDialog}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isTwoColumnLayout ? 'row' : 'column',
                    gap: 2,
                    p: 0.2,
                    alignItems: fillsViewportOnLargeScreen ? 'stretch' : 'flex-start',
                    ...(fillsViewportOnLargeScreen ? { flex: 1, minHeight: 0, overflow: 'hidden' } : {}),
                }}
            >
                {/* 左侧：趋势图 */}
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        width: isTwoColumnLayout ? undefined : '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: 'var(--fill-neutral-100)',
                        borderRadius: '12px',
                        border: '1px solid var(--stroke-neutral-800-a25)',
                        p: 4,
                        gap: 2,
                        ...(fillsViewportOnLargeScreen ? { minHeight: 0 } : {}),
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexShrink: 0 }}>
                        <Typography
                            sx={{
                                fontSize: 20,
                                lineHeight: '24px',
                                fontWeight: 500,
                                color: 'var(--text-dark)',
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            {i18n.t('group_revenue_analysis_menu_sales_trend_title')}
                            {' — '}
                            {chartTitleMenuName}
                        </Typography>
                        <CommonInput
                            inputType="SELECT_PILL"
                            size="small"
                            items={METRIC_OPTIONS.map((o) => ({
                                value: o.value,
                                label: i18n.t(o.label),
                            }))}
                            value={state.dishSalesMetric}
                            onChange={(e) => state.setDishSalesMetric(e.target.value as MenuSalesMetric)}
                            sx={{ flexShrink: 0, minWidth: 200 }}
                        />
                    </Box>

                    {lineChartSeries.length > 0 ? (
                        <Box
                            sx={{
                                position: 'relative',
                                ...(fillsViewportOnLargeScreen
                                    ? { flex: 1, minHeight: GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX }
                                    : {
                                        minHeight: GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX,
                                        height: GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX,
                                    }),
                            }}
                        >
                            <LinechartOfBusinessData
                                series={lineChartSeries}
                                apexOptions={dishLineApexOptions}
                                height={fillsViewportOnLargeScreen ? '100%' : GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX}
                                rootId="line-chart-of-business-data-compare"
                                rawXLabels={lineChartRawXLabels}
                            />
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                ...(fillsViewportOnLargeScreen
                                    ? { flex: 1, minHeight: GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX }
                                    : { minHeight: GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX }),
                            }}
                        >
                            <Typography variant="body2" sx={{ color: chartLabelColor }}>
                                {i18n.t('general_text_no_data')}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* 右侧：门店菜单营收排名；大屏与左折线卡等高；窄屏单列全宽堆叠在折线下方 */}
                <Box
                    sx={{
                        width: isTwoColumnLayout ? 360 : '100%',
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: 'var(--fill-neutral-100)',
                        borderRadius: '16px',
                        border: '1px solid var(--stroke-neutral-800-a25)',
                        p: 2,
                        gap: 1.5,
                        ...(fillsViewportOnLargeScreen ? { minHeight: 0, alignSelf: 'stretch' } : {}),
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PillSelectStoreIcon color='var(--icon-primary-300)' style={{ width: 24, height: 24 }} />
                            <Typography
                                sx={{
                                    fontSize: 20,
                                    lineHeight: '24px',
                                    fontWeight: 500,
                                    color: 'var(--text-primary-300)',
                                    textAlign: 'center',
                                }}
                            >
                                {i18n.t('group_revenue_analysis_menu_store_revenue_rank')}
                            </Typography>
                        </Box>
                        {chartTitleMenuName ? (
                            <Typography
                                sx={{
                                    fontSize: 20,
                                    lineHeight: '24px',
                                    fontWeight: 500,
                                    color: 'var(--text-primary-300)',
                                    textAlign: 'center',
                                }}
                            >
                                {chartTitleMenuName}
                            </Typography>
                        ) : null}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            pr: 0.5,
                            ...(fillsViewportOnLargeScreen
                                ? { flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }
                                : {}),
                        }}
                    >
                        {menuStoreRevenueRankingBars == null ? (
                            <Typography variant="body2" sx={{ color: chartLabelColor, textAlign: 'center', py: 4 }}>
                                {i18n.t('general_text_no_data')}
                            </Typography>
                        ) : (
                            menuStoreRevenueRankingBars
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
});

/** 骨架屏 props：顶部筛选由调用方传入，加载时仍可操作 */
interface GroupDishSalesAnalysisSkeletonProps {
    filterRow: React.ReactNode;
    /** 是否大屏拉满内容区（与主页面一致） */
    fillsViewportOnLargeScreen: boolean;
    /** 是否宽屏双栏（≥1280px） */
    isTwoColumnLayout: boolean;
}

/**
 * 集团菜单销售分析 Tab 加载骨架：筛选行 + 左图右榜布局（窄屏单列）
 */
function GroupDishSalesAnalysisSkeleton({
    filterRow,
    fillsViewportOnLargeScreen,
    isTwoColumnLayout,
}: GroupDishSalesAnalysisSkeletonProps): React.ReactElement {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2,
                ...(fillsViewportOnLargeScreen ? { flex: 1, minHeight: 0, overflow: 'hidden' } : {}),
            }}
        >
            {filterRow}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isTwoColumnLayout ? 'row' : 'column',
                    gap: 2,
                    alignItems: fillsViewportOnLargeScreen ? 'stretch' : 'flex-start',
                    ...(fillsViewportOnLargeScreen ? { flex: 1, minHeight: 0, overflow: 'hidden' } : {}),
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        width: isTwoColumnLayout ? undefined : '100%',
                        bgcolor: 'var(--fill-neutral-100)',
                        borderRadius: '12px',
                        border: '1px solid var(--stroke-neutral-800-a25)',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        ...(fillsViewportOnLargeScreen ? { minHeight: 0 } : {}),
                    }}
                >
                    <Skeleton variant="text" width="70%" height={28} />
                    <Skeleton
                        variant="rounded"
                        sx={{
                            borderRadius: 2,
                            ...(fillsViewportOnLargeScreen
                                ? { flex: 1, minHeight: GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX }
                                : { height: GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX }),
                        }}
                    />
                </Box>
                <Box
                    sx={{
                        width: isTwoColumnLayout ? 360 : '100%',
                        flexShrink: 0,
                        bgcolor: 'var(--fill-neutral-100)',
                        border: '1px solid var(--stroke-neutral-800-a25)',
                        borderRadius: '16px',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        ...(fillsViewportOnLargeScreen ? { minHeight: 0, alignSelf: 'stretch' } : {}),
                    }}
                >
                    <Skeleton variant="text" width="85%" height={24} sx={{ alignSelf: 'center' }} />
                    <Skeleton variant="text" width="60%" height={24} sx={{ alignSelf: 'center' }} />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            ...(fillsViewportOnLargeScreen ? { flex: 1, minHeight: 0 } : {}),
                        }}
                    >
                        {['a', 'b', 'c', 'd', 'e', 'f'].map((k) => (
                            <Skeleton key={k} variant="rounded" height={56} sx={{ borderRadius: '16px' }} />
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default GroupDishSalesAnalysis;
